/**
 * Hooks para VEN-01 (Clientes), VEN-02 (Productos/Servicios),
 * VEN-03/04/05 (Documentos), VEN-06 (Suscripciones), VEN-07 (Cobranza), VEN-08 (Dashboard)
 */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

/** Importar un archivo puede tardar minutos: el timeout por defecto (30 s) corta
 *  el request mientras el servidor sigue escribiendo. */
const IMPORT_TIMEOUT_MS = 300_000
export { useDriverInfo } from '@/modules/config/hooks/useConfig'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Customer {
  id: string
  legal_name: string
  trade_name: string
  tax_id: string
  /** Catálogo 06 de SUNAT (solo Perú). Vacío = deducir por longitud. */
  tax_id_type?: string
  tax_id_label: string
  person_type: 'juridica' | 'natural'
  person_type_display: string
  email_contact: string
  email_dte: string
  phone: string
  contact_name: string
  giro: string
  address_street: string
  address_commune: string
  address_city: string
  address_region: string
  address_country: string
  credit_limit: string | null
  payment_term_days: number
  /** Centro de costo heredado cuando el producto no define uno. */
  default_cost_center?: string | null
  notes: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CustomerListItem {
  id: string
  legal_name: string
  trade_name: string
  tax_id: string
  person_type: string
  email_contact: string
  phone: string
  giro: string
  address_street: string
  address_commune: string
  address_city: string
  is_active: boolean
  created_at: string
}

export interface CustomerListResponse {
  customers: CustomerListItem[]
  count: number
  page: number
  page_size: number
  pages: number
}

/**
 * Monedas en las que puede estar el precio de un producto o de una linea.
 *
 * ⚠️ Faltaba **PEN**, asi que un producto peruano no se podia tipar en soles.
 * El `''` es una linea que NO declara moneda: el backend la resuelve contra la
 * base del tenant (`normalize()` en `localization/currency.py`), que es lo
 * correcto — mandar `'CLP'` desde el formulario le ponia esa etiqueta a las
 * lineas de cualquier pais.
 *
 * Espeja `Product.PRICE_CURRENCY_CHOICES` del backend; que aparezca acá no
 * significa que se ofrezca en todos los paises: eso lo decide
 * `useForeignCurrencies()` en `lib/locale.ts` (la UF es chilena).
 */
export type PriceCurrency = '' | 'CLP' | 'PEN' | 'USD' | 'UF' | 'EUR' | 'JPY'

// Precio de un producto en una moneda ADICIONAL a la del precio por defecto.
export interface ProductPrice {
  currency: PriceCurrency
  currency_display?: string
  unit_price: string
}

export interface AccountMini {
  id: string
  code: string
  name: string
}

export interface Product {
  id: string
  sku: string
  name: string
  description: string
  item_type: 'product' | 'service'
  item_type_display: string
  unit: string
  unit_display: string
  unit_price: string
  price_currency: PriceCurrency
  price_currency_display: string
  prices?: ProductPrice[]
  is_tax_exempt: boolean
  tax_data: Record<string, unknown>
  tax_fields: TaxField[]
  /** Decide SI el producto comisiona. Sin esto, la tasa nunca se aplica. */
  is_commissionable?: boolean
  commission_rate: string
  commission_base?: 'total' | 'margin'
  margin_rate?: string
  /** Código del catálogo 54 de SUNAT. Solo Perú; vacío en los demás países. */
  detraction_code?: string
  track_stock?: boolean
  reorder_point?: number | string | null
  barcode?: string | null
  category?: string | null
  category_name?: string | null
  attributes?: Record<string, string>
  sales_account: AccountMini | null
  purchase_account: AccountMini | null
  /** Centro al que se imputa el producto si la línea no trae uno propio. */
  default_cost_center?: string | null
  default_cost_center_code?: string | null
  default_cost_center_name?: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface ProductListItem {
  id: string
  sku: string
  name: string
  description?: string
  item_type: string
  item_type_display: string
  unit: string
  unit_display: string
  unit_price: string
  price_currency?: PriceCurrency
  price_currency_display?: string
  prices?: ProductPrice[]
  is_tax_exempt: boolean
  is_commissionable?: boolean
  commission_rate?: string
  commission_base?: 'total' | 'margin'
  margin_rate?: string
  /** Código del catálogo 54 de SUNAT. Solo Perú; vacío en los demás países. */
  detraction_code?: string
  track_stock?: boolean
  /** Stock disponible (suma de todas las bodegas). null si no controla stock. */
  available_stock?: string | null
  barcode?: string | null
  barcode_type?: string
  category?: string | null
  category_name?: string | null
  /** Valores de los atributos dinamicos de la categoria ({code: valor}).
   *  En un catalogo de repuestos lleva la procedencia, el codigo original,
   *  las equivalencias por fabricante y a que vehiculos aplica: es lo que
   *  deja ver POR QUE un repuesto es el que sirve, sin abrir la ficha. */
  attributes?: Record<string, unknown>
  sales_account: AccountMini | null
  purchase_account: AccountMini | null
  default_cost_center?: string | null
  default_cost_center_code?: string | null
  default_cost_center_name?: string | null
  is_active: boolean
  created_at: string
}

export interface ProductListResponse {
  products: ProductListItem[]
  count: number
  page: number
  page_size: number
  pages: number
}

export interface TaxField {
  key: string
  label: string
  type: string
  default?: unknown
  help_text?: string
}

// ─── VEN-03/04/05: Documentos ─────────────────────────────────────────────────

export interface DocumentReferencia {
  tipo_doc: string        // TpoDocRef: '33', '56', '61', '801', etc.
  folio_ref: string       // FolioRef
  fecha_ref: string       // FchRef (YYYY-MM-DD)
  cod_ref?: string        // CodRef: '1'=anula, '2'=corrige texto, '3'=corrige monto
  razon_ref?: string      // RazonRef
}

export interface DocumentPersonalizado {
  tipo: 'string' | 'numero' | 'fecha' | 'select' | 'multiselect' | 'boolean' | 'entity_link'
  nombre: string
  valor: string
  valor_label?: string
  link_entity?: string
}

export interface CostCenterAllocation {
  cost_center_id: string
  percentage: string
}

export interface SaleDocumentLine {
  product_id?: string | null
  sku: string
  name: string
  description?: string      // DscItem
  unit: string
  quantity: string
  unit_price: string
  price_currency?: PriceCurrency
  unit_price_original?: string  // Precio original en moneda extranjera
  discount_pct?: string     // DescuentoPct (default '0')
  is_tax_exempt: boolean
  tax_data: Record<string, unknown>
  subtotal?: string
  cost_center_allocations?: CostCenterAllocation[]
}

export interface EmissionLog {
  id: number
  event: string
  status: 'success' | 'info' | 'error'
  message: string
  metadata: Record<string, unknown>
  actor?: string | null
  actor_name?: string | null
  created_at: string
}

export interface SaleDocument {
  id: string
  customer: string
  /** Guía de despacho (52): lo declarado, de solo lectura. Se escribe con `ind_traslado` y `transporte`. */
  guia?: { ind_traslado: string; transporte: Record<string, string> }
  ind_traslado?: string
  transporte?: Record<string, string>
  customer_name: string
  document_type: string
  document_type_display: string
  status: 'draft' | 'processing' | 'xml_generated' | 'xml_signed' | 'pending_send' | 'sent_facturador' | 'querying_status' | 'accepted' | 'rejected' | 'cancelled'
  status_display: string
  lines: SaleDocumentLine[]
  subtotal: string
  tax_amount: string
  total: string
  tax_rate: string
  /**
   * Moneda en la que se EMITE el comprobante. Vacio = la moneda base del
   * tenant, que es el caso de todo lo que existe hoy.
   *
   * ⚠️ Los `*_original` son los importes en ESA moneda —los que viajan en el
   * XML— mientras `subtotal`/`tax_amount`/`total` siguen en la moneda base,
   * que es lo que leen cobranza, contabilidad y los reportes.
   */
  currency?: string
  exchange_rate?: string | null
  subtotal_original?: string | null
  tax_amount_original?: string | null
  total_original?: string | null
  issue_date: string
  due_date?: string | null  // FchVenc
  payment_method?: 'contado' | 'credito'
  payment_term_days?: number
  notes: string
  referencias: DocumentReferencia[]
  personalizados: DocumentPersonalizado[]
  exchange_rate_data?: Record<string, string>
  localization_data: Record<string, unknown>
  reference_document: string | null
  folio: string | null
  emission_logs?: EmissionLog[]
  collection_status?: string
  accounting_status?: 'pending' | 'accounted'
  payments?: DocumentPaymentAllocation[]
  sales_order?: string | null
  sales_order_number?: string | null
  attachments?: SalesAttachment[]
  order_attachments?: SalesAttachment[]
  outstanding?: string
  adjustment_status?: 'normal' | 'adjusted' | 'annulled' | 'annulled_after_payment'
  adjustments?: Array<{
    id: string
    type: 'NC' | 'ND'
    document_type: string
    folio: string
    issue_date: string
    amount: string
    reason: string
  }>
  seller_name?: string | null
  contract?: string | null
  contract_number?: string | null
  contract_title?: string | null
  created_by?: string | null
  created_by_name?: string | null
  emitted_by?: string | null
  emitted_by_name?: string | null
  emitted_at?: string | null
  last_sent_by_name?: string | null
  last_sent_at?: string | null
  cancelled_by_name?: string | null
  cancelled_at?: string | null
  customer_tax_id?: string
  created_at: string
  updated_at: string
}

// ─── Plantillas de Documento ──────────────────────────────────────────────────

export interface DocumentTemplate {
  id: string
  name: string
  description: string
  customer: string | null
  customer_name: string
  document_type: string
  document_type_display: string
  lines: SaleDocumentLine[]
  notes: string
  personalizados: DocumentPersonalizado[]
  payment_method: 'contado' | 'credito'
  payment_method_display?: string
  payment_term_days: number
  is_active: boolean
  created_by?: string | null
  created_at: string
  updated_at?: string
}

export interface DocumentTemplateListResponse {
  templates: DocumentTemplate[]
  count: number
  page: number
  page_size: number
  pages: number
}

// ─── Voucher Preview ──────────────────────────────────────────────────────────

export interface VoucherPreviewLine {
  account_code: string
  account_name: string
  glosa: string
  debe: string
  haber: string
  cost_center_code?: string | null
  cost_center_name?: string | null
}

export interface VoucherPreviewResponse {
  lines: VoucherPreviewLine[]
  totals: { total_debe: string; total_haber: string }
  warning?: string
}

export interface DocumentPaymentAllocation {
  id: string
  payment_id: string
  amount: string
  payment_date: string
  payment_method: string
  payment_method_display: string
  payment_status: string
  payment_status_display: string
  reference: string
  customer_name: string
  payment_total: string
  bank_account_display?: string | null
  created_at: string
}

export type DocumentStatus = SaleDocument['status']

export interface CSVImportResult {
  created: number
  updated: number
  errors: { row: number; error: string | Record<string, string[]> }[]
  total_rows: number
}

export interface SaleDocumentListItem {
  id: string
  customer: string
  customer_name: string
  customer_tax_id: string
  document_type: string
  document_type_display: string
  status: DocumentStatus
  status_display: string
  total: string
  issue_date: string
  due_date: string | null
  folio: string | null
  accounting_status?: 'pending' | 'accounted' | 'not_applicable'
  sales_order_number?: string | null
  collection_status?: '' | 'pending' | 'partial' | 'paid' | 'overdue'
  collection_status_display?: string
  outstanding_amount?: string
  adjustment_status?: 'normal' | 'adjusted' | 'annulled' | 'annulled_after_payment'
  contract?: string | null
  contract_number?: string | null
  created_at: string
}

export interface SaleDocumentListResponse {
  documents: SaleDocumentListItem[]
  count: number
  page: number
  page_size: number
  pages: number
}

// ─── PED-01: Pedidos de Venta ────────────────────────────────────────────────

export type SalesOrderStatus = 'draft' | 'approved' | 'invoiced' | 'cancelled'

export interface SalesOrderListItem {
  id: string
  order_number: string
  customer: string
  customer_name: string
  customer_tax_id: string
  seller: string
  seller_name: string
  status: SalesOrderStatus
  status_display: string
  target_document_type: string
  total: string
  commission_total: string
  collection_contacts: string[]
  order_date: string
  expected_delivery_date: string | null
  created_at: string
}

export interface SalesOrder extends SalesOrderListItem {
  lines: SaleDocumentLine[]
  subtotal: string
  tax_amount: string
  tax_rate: string
  commission_data: {
    product_id: string | null
    sku: string
    commission_base: string
    commission_rate: string
    margin_rate: string
    line_net: string
    margin_amount: string
    commission_amount: string
  }[]
  payment_method: 'contado' | 'credito'
  payment_term_days: number
  notes: string
  exchange_rate_data: Record<string, string>
  lock_exchange_rate?: boolean
  personalizados: DocumentPersonalizado[]
  referencias: DocumentReferencia[]
  collection_contacts: string[]
  cancellation_reason?: string
  approved_by?: string | null
  approved_by_name?: string | null
  approved_at?: string | null
  invoices?: { id: string; folio: string | null; document_type: string; status: string; total: string }[]
  attachments?: SalesAttachment[]
  updated_at: string
}

export interface SalesOrderListResponse {
  orders: SalesOrderListItem[]
  count: number
  page: number
  page_size: number
  pages: number
}

export interface SalesAttachment {
  id: string
  file_name: string
  file_size: number
  content_type: string
  download_url: string
  uploaded_by: string | null
  uploaded_by_name: string | null
  created_at: string
}

export interface AttachmentListResponse {
  count: number
  attachments: SalesAttachment[]
}

export interface CommissionSellerSummary {
  seller_id: string
  seller_name: string
  orders_count: number
  invoiced_count: number
  total_sales: string
  total_commissions: string
  paid_amount: string
  pending_amount: string
}

export interface CommissionDashboardData {
  sellers: CommissionSellerSummary[]
  totals: { total_sales: string; total_commissions: string }
}

export interface CommissionHistoryEntry {
  id: number
  timestamp: string
  actor_email: string | null
  actor_name: string | null
  action: number  // 0=create, 1=update, 2=delete
  changes: Record<string, [string | null, string | null]>
}

export interface CommissionHistoryResponse {
  product_name: string
  results: CommissionHistoryEntry[]
}

// ─── Query Keys ───────────────────────────────────────────────────────────────

export const salesKeys = {
  customers: (params?: object) => ['sales', 'customers', params] as const,
  customer: (id: string) => ['sales', 'customers', id] as const,
  products: (params?: object) => ['sales', 'products', params] as const,
  product: (id: string) => ['sales', 'products', id] as const,
  documents: (params?: object) => ['sales', 'documents', params] as const,
  document: (id: string) => ['sales', 'documents', id] as const,
  templates: (params?: object) => ['sales', 'templates', params] as const,
  template: (id: string) => ['sales', 'templates', id] as const,
  voucherPreview: (id: string) => ['sales', 'voucher-preview', id] as const,
  orders: (params?: object) => ['sales', 'orders', params] as const,
  order: (id: string) => ['sales', 'orders', id] as const,
  commissions: (params?: object) => ['sales', 'commissions', params] as const,
  forecast: (params?: object) => ['sales', 'forecast', params] as const,
  orderAttachments: (orderId: string) => ['sales', 'orders', orderId, 'attachments'] as const,
  documentAttachments: (documentId: string) => ['sales', 'documents', documentId, 'attachments'] as const,
}

// ─── VEN-01: Clientes ─────────────────────────────────────────────────────────

export function useCustomers(params?: {
  search?: string
  active?: string
  page?: number
  page_size?: number
  ordering?: string
  seller_own?: 'true' | 'false'
}) {
  return useQuery<CustomerListResponse>({
    queryKey: salesKeys.customers(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/customers/', { params })
      return res.data
    },
  })
}

export function useCustomer(id: string) {
  return useQuery<Customer>({
    queryKey: salesKeys.customer(id),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/customers/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Customer>) => {
      const res = await apiClient.post('/sales/customers/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'customers'] })
    },
  })
}

export function useUpdateCustomer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Customer>) => {
      const res = await apiClient.patch(`/sales/customers/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'customers'] })
      qc.invalidateQueries({ queryKey: salesKeys.customer(id) })
    },
  })
}

export function useDeactivateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/sales/customers/${id}/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'customers'] })
    },
  })
}

export function useRestoreCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/customers/${id}/restore/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'customers'] })
    },
  })
}

export function useImportCustomersCSV() {
  const qc = useQueryClient()
  return useMutation<CSVImportResult, Error, { file: File; mapping: Record<string, string> }>({
    mutationFn: async ({ file, mapping }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('mapping', JSON.stringify(mapping))
      const res = await apiClient.post('/sales/customers/import-csv/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // ⚠️ El default de 30 s es correcto para un listado y absurdo para importar
        // cientos de filas: una importación real de 745 clientes tardó 35 s y el
        // navegador mostró «timeout» sobre un archivo que SÍ se había importado
        // completo — el peor error posible, porque invita a reintentar.
        timeout: IMPORT_TIMEOUT_MS,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'customers'] })
    },
  })
}

// ─── VEN-02: Productos ────────────────────────────────────────────────────────

export function useProducts(params?: {
  search?: string
  active?: string
  type?: string
  category?: string
  attr?: string[]
  page?: number
  page_size?: number
  ordering?: string
}) {
  return useQuery<ProductListResponse>({
    queryKey: salesKeys.products(params),
    queryFn: async () => {
      // indexes:null → `attr` repetido sin corchetes (compatible con getlist('attr') en Django).
      const res = await apiClient.get('/sales/products/', { params, paramsSerializer: { indexes: null } })
      return res.data
    },
  })
}

export function useProduct(id: string) {
  return useQuery<Product>({
    queryKey: salesKeys.product(id),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/products/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Product>) => {
      const res = await apiClient.post('/sales/products/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'products'] })
    },
  })
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Product>) => {
      const res = await apiClient.patch(`/sales/products/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'products'] })
      qc.invalidateQueries({ queryKey: salesKeys.product(id) })
    },
  })
}

export function useDeactivateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/sales/products/${id}/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'products'] })
    },
  })
}

export function useRestoreProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/products/${id}/restore/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'products'] })
    },
  })
}

export function useImportProductsCSV() {
  const qc = useQueryClient()
  return useMutation<CSVImportResult, Error, { file: File; mapping: Record<string, string> }>({
    mutationFn: async ({ file, mapping }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('mapping', JSON.stringify(mapping))
      const res = await apiClient.post('/sales/products/import-csv/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // ⚠️ El default de 30 s es correcto para un listado y absurdo para importar
        // cientos de filas: una importación real de 745 clientes tardó 35 s y el
        // navegador mostró «timeout» sobre un archivo que SÍ se había importado
        // completo — el peor error posible, porque invita a reintentar.
        timeout: IMPORT_TIMEOUT_MS,
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'products'] })
    },
  })
}

// ─── VEN-03/04/05: Documentos ─────────────────────────────────────────────────

export function useSaleDocuments(params?: {
  search?: string
  status?: string
  type?: string
  customer_id?: string
  contract?: string
  personalizados?: string
  accounting_status?: string
  date_from?: string
  date_to?: string
  total_min?: string
  total_max?: string
  ordering?: string
  page?: number
  page_size?: number
}, opts?: { keepPrevious?: boolean }) {
  return useQuery<SaleDocumentListResponse>({
    queryKey: salesKeys.documents(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/documents/', { params })
      return res.data
    },
    // opt-in: al cambiar params (ej. crecer page_size en el rail del 360) mantiene
    // los datos previos en vez de vaciar la lista mientras recarga (evita parpadeo).
    ...(opts?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  })
}

// Fase 4: facturar desde guías de despacho. Crea una factura borrador que
// consolida y referencia las guías seleccionadas. Devuelve el documento creado.
export function useInvoiceFromGuides() {
  const qc = useQueryClient()
  return useMutation<SaleDocument, Error, { guide_ids: string[]; document_type?: string }>({
    mutationFn: async (payload) => {
      const res = await apiClient.post('/sales/guides/invoice/', payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

export function useSaleDocument(id: string | null, opts?: { keepPrevious?: boolean }) {
  return useQuery<SaleDocument>({
    queryKey: salesKeys.document(id ?? ''),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/documents/${id}/`)
      return res.data
    },
    enabled: !!id,
    staleTime: 0,
    // Opt-in: al cambiar de documento (Vista 360 navegando por el rail) conserva
    // el doc previo en pantalla mientras carga el nuevo → no hay flash de spinner
    // que desmonte el shell/rail y reinicie el scroll. NO usar en modales (el id
    // cambia al reabrir y mostraría data vieja por un instante).
    ...(opts?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  })
}

export function useCreateDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<SaleDocument>) => {
      const res = await apiClient.post('/sales/documents/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

export function useCloneDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/documents/${id}/duplicate/`)
      return res.data as SaleDocument
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

export function useUpdateDocument(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<SaleDocument>) => {
      const res = await apiClient.patch(`/sales/documents/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
      qc.invalidateQueries({ queryKey: salesKeys.document(id) })
    },
  })
}

/**
 * Edita campos NO contables (personalizados, notas) en CUALQUIER estado.
 * No afecta montos ni contabilidad → no requiere status='draft'.
 */
export function useUpdateDocumentMetadata(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { personalizados?: unknown[]; notes?: string }) => {
      const res = await apiClient.patch(`/sales/documents/${id}/metadata/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
      qc.invalidateQueries({ queryKey: salesKeys.document(id) })
    },
  })
}

export function useEmitDocument() {
  const qc = useQueryClient()
  return useMutation({
    // retry: 0 explícito (#369): un reintento automático del POST /emit/ podría
    // encolar una segunda emisión. El backend ya es idempotente (claim atómico
    // draft→processing), pero evitamos depender solo de eso.
    retry: 0,
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/documents/${id}/emit/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

export function useCancelDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const res = await apiClient.post(`/sales/documents/${id}/cancel/`, { reason })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

export function useDeleteDraftDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/sales/documents/${id}/delete-draft/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

export function useAdjustDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SaleDocument> }) => {
      const res = await apiClient.post(`/sales/documents/${id}/adjust/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

export function useRetryDocumentSend() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/documents/${id}/retry-send/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

// ─── VEN-08: Dashboard ────────────────────────────────────────────────────────

export interface DashboardMetrics {
  currency: string
  period: { month: number; year: number; date_from: string; date_to: string }
  documents: {
    total_this_month: number
    amount_this_month: string
    by_status: Record<string, { count: number; total: string }>
  }
  customers: { active: number; new_this_month: number }
  collections: {
    total_outstanding: string
    overdue: string
    collected_this_month: string
  }
  top_debtors: Array<{ customer_id: string; customer_name: string; outstanding: string }>
  recent_documents: Array<{
    id: string
    customer_name: string
    document_type: string
    status: string
    total: string
    folio: string | null
    issue_date: string | null
  }>
  purchases_kpis?: {
    invoiced_this_month: string
    pending_payable: string
    paid_this_month: string
    overdue_payable: number
  }
  trend_6m?: Array<{
    month: string
    sales_total: string
    purchases_total: string
    collections_total: string
  }>
  balance?: {
    /** Saldo por cobrar **a la fecha**: no depende del rango del dashboard. */
    total_receivable: string
    total_payable: string
    net_position: string
    /** Cuánto del saldo por cobrar está vencido (mismo corte que el total). */
    overdue_receivable?: string
    receivable_count?: number
    receivable_overdue_count?: number
    payable_count?: number
    /** Fecha de corte de los saldos. Va en el tooltip y en el nombre del Excel. */
    as_of_date?: string
  }
  top_customers?: Array<{
    customer_name: string
    total: string
    doc_count: number
  }>
  /** Conteo y resultado neto sobre TODOS los centros (no solo el top 10 de la tabla). */
  cost_centers_summary?: {
    count: number
    net_result: string
  }
  cost_center_distribution?: Array<{
    cost_center_id: string
    code: string
    name: string
    total_debe: string
    total_haber: string
  }>
}

export function useDashboardMetrics(params?: { date_from?: string; date_to?: string }) {
  return useQuery<DashboardMetrics>({
    queryKey: ['sales', 'dashboard', params],
    queryFn: async () => {
      const res = await apiClient.get('/sales/dashboard/', { params })
      return res.data
    },
    staleTime: 30 * 1000,
  })
}

// ─── VEN-07: Cobranza ─────────────────────────────────────────────────────────

export interface PaymentAllocation {
  id: string
  invoice: string
  invoice_folio: string | null
  amount: string
  created_at: string
}

export interface CustomerPayment {
  id: string
  customer: string
  customer_name: string
  payment_date: string
  amount: string
  allocated_amount?: string
  payment_method: 'transfer' | 'check' | 'cash' | 'card' | 'other'
  payment_method_display?: string
  status: 'registered' | 'confirmed' | 'cancelled'
  status_display?: string
  reference: string
  notes: string
  bank_account?: string | null
  bank_account_display?: string | null
  accounting_status?: 'pending' | 'accounted'
  allocations: PaymentAllocation[]
  created_by_name?: string | null
  created_at: string
  updated_at?: string
}

export interface CustomerPaymentListResponse {
  payments: CustomerPayment[]
  count: number
  page: number
  page_size: number
  pages: number
}

export type AdjustmentStatus = 'normal' | 'adjusted' | 'annulled' | 'annulled_after_payment'

export interface DocumentAdjustment {
  id: string
  type: 'NC' | 'ND'
  document_type: string
  folio: string
  issue_date: string
  amount: string
  reason: string
}

/**
 * Desglose de la detracción (SPOT, solo Perú). `null` en el resto de los países.
 *
 * 🔴 NO reduce el saldo por cobrar: el PCGE 2020 la trata como una
 * reclasificación —la cuenta por cobrar se cancela por el TOTAL, repartido entre
 * el banco del proveedor y su cuenta en el Banco de la Nación—. Esto sirve para
 * MOSTRAR por dónde entra cada parte.
 */
export interface DetraccionInfo {
  codigo: string
  descripcion: string
  porcentaje: string
  monto: string
  monto_pen: string
  /** Lo que el cliente transfiere: total menos detracción. */
  neto_a_cobrar: string
  cuenta_banco_nacion: string
}

export interface CollectionInvoice {
  id: string
  customer: string
  customer_name: string
  customer_tax_id: string
  folio: string | null
  issue_date: string | null
  due_date: string | null
  total: string
  adjusted_total?: string
  paid_amount: string
  outstanding: string
  collection_status: 'pending' | 'partial' | 'paid' | 'overdue'
  adjustment_status?: AdjustmentStatus
  adjustments?: DocumentAdjustment[]
  is_ceded?: boolean
  detraction?: DetraccionInfo | null
}

export interface CollectionsListResponse {
  invoices: CollectionInvoice[]
  count: number
  page: number
  page_size: number
  pages: number
}

export interface ArAgingBucket {
  count: number
  total: string
}

export interface ArAgingResponse {
  buckets: {
    current: ArAgingBucket
    '1_30': ArAgingBucket
    '31_60': ArAgingBucket
    '61_90': ArAgingBucket
    over_90: ArAgingBucket
  }
  total_outstanding: string
  invoices: Array<{
    id: string
    customer_name: string
    folio: string | null
    due_date: string | null
    total: string
    outstanding: string
    days_overdue: number
  }>
}

export const collectionsKeys = {
  payments: (params?: object) => ['sales', 'payments', params] as const,
  payment: (id: string) => ['sales', 'payments', id] as const,
  collections: (params?: object) => ['sales', 'collections', params] as const,
  arAging: () => ['sales', 'collections', 'ar-aging'] as const,
}

export function useCustomerPayments(params?: {
  customer_id?: string
  document_id?: string
  status?: string
  accounting_status?: string
  search?: string
  date_from?: string
  date_to?: string
  ordering?: string
  page?: number
  page_size?: number
}) {
  return useQuery<CustomerPaymentListResponse>({
    queryKey: collectionsKeys.payments(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/payments/', { params })
      return res.data
    },
  })
}

export function useCustomerPayment(id: string) {
  return useQuery<CustomerPayment>({
    queryKey: collectionsKeys.payment(id),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/payments/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateCustomerPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<CustomerPayment>) => {
      const res = await apiClient.post('/sales/payments/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'payments'] })
      qc.invalidateQueries({ queryKey: ['sales', 'collections'] })
      qc.invalidateQueries({ queryKey: ['sales', 'dashboard'] })
    },
  })
}

export function useUpdatePayment(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<CustomerPayment>) => {
      const res = await apiClient.patch(`/sales/payments/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'payments'] })
      qc.invalidateQueries({ queryKey: collectionsKeys.payment(id) })
      qc.invalidateQueries({ queryKey: ['sales', 'collections'] })
      qc.invalidateQueries({ queryKey: ['sales', 'dashboard'] })
    },
  })
}

export function useConfirmPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/payments/${id}/confirm/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'payments'] })
      qc.invalidateQueries({ queryKey: ['sales', 'collections'] })
      qc.invalidateQueries({ queryKey: ['sales', 'dashboard'] })
    },
  })
}

export function useCancelPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/payments/${id}/cancel/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'payments'] })
      qc.invalidateQueries({ queryKey: ['sales', 'collections'] })
      qc.invalidateQueries({ queryKey: ['sales', 'dashboard'] })
    },
  })
}

export function useCollections(params?: {
  collection_status?: string
  customer_id?: string
  search?: string
  date_from?: string
  date_to?: string
  ordering?: string
  aging_bucket?: string
  page?: number
  page_size?: number
}) {
  return useQuery<CollectionsListResponse>({
    queryKey: collectionsKeys.collections(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/collections/', { params })
      return res.data
    },
  })
}

export function useArAging() {
  return useQuery<ArAgingResponse>({
    queryKey: collectionsKeys.arAging(),
    queryFn: async () => {
      const res = await apiClient.get('/sales/collections/ar-aging/')
      return res.data
    },
    staleTime: 60 * 1000,
  })
}

// ─── VEN-07b: Bitácora de Cobranza ───────────────────────────────────────────

export interface CollectionLog {
  id: string
  document: string
  action_type: 'call' | 'email' | 'message' | 'visit' | 'letter' | 'agreement' | 'other'
  action_type_display: string
  description: string
  contact_name: string
  result: string
  next_action_date: string | null
  created_by: string | null
  created_by_name: string
  created_at: string
}

export const collectionLogKeys = {
  logs: (docId: string) => ['sales', 'collection-logs', docId] as const,
}

export function useCollectionLogs(documentId: string) {
  return useQuery<CollectionLog[]>({
    queryKey: collectionLogKeys.logs(documentId),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/collections/${documentId}/logs/`)
      return res.data
    },
    enabled: !!documentId,
  })
}

export function useCreateCollectionLog() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ documentId, data }: { documentId: string; data: Partial<CollectionLog> }) => {
      const res = await apiClient.post(`/sales/collections/${documentId}/logs/`, data)
      return res.data
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: collectionLogKeys.logs(variables.documentId) })
    },
  })
}

export function useDeleteCollectionLog() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ documentId, logId }: { documentId: string; logId: string }) => {
      await apiClient.delete(`/sales/collections/${documentId}/logs/${logId}/`)
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: collectionLogKeys.logs(variables.documentId) })
    },
  })
}

// ─── VEN-06: Suscripciones ────────────────────────────────────────────────────

export interface RecurringInvoiceLine {
  sku: string
  name: string
  unit: string
  quantity: string
  unit_price: string
}

export interface RecurringInvoice {
  id: string
  customer: string
  customer_name: string
  contract?: string | null
  contract_number?: string | null
  document_type: 'FE' | 'BE' | 'NC' | 'ND'
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'annual'
  frequency_display?: string
  day_of_month: number
  lines: RecurringInvoiceLine[]
  notes: string
  status: 'active' | 'paused' | 'cancelled'
  status_display?: string
  generate_as: 'draft' | 'auto_emit'
  next_generation_date: string
  end_date?: string | null
  last_generated_at: string | null
  estimated_subtotal?: string
  estimated_tax?: string
  estimated_total?: string
  personalizados?: Record<string, string>
  created_at: string
  updated_at?: string
}

export interface RecurringInvoiceListResponse {
  subscriptions: RecurringInvoice[]
  count: number
  page: number
  page_size: number
  pages: number
}

export const subscriptionKeys = {
  list: (params?: object) => ['sales', 'subscriptions', params] as const,
  detail: (id: string) => ['sales', 'subscriptions', id] as const,
}

export function useRecurringInvoices(params?: {
  status?: string
  customer_id?: string
  page?: number
  page_size?: number
  ordering?: string
}) {
  return useQuery<RecurringInvoiceListResponse>({
    queryKey: subscriptionKeys.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/subscriptions/', { params })
      return res.data
    },
  })
}

export function useRecurringInvoice(id: string) {
  return useQuery<RecurringInvoice>({
    queryKey: subscriptionKeys.detail(id),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/subscriptions/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateRecurringInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<RecurringInvoice>) => {
      const res = await apiClient.post('/sales/subscriptions/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'subscriptions'] })
    },
  })
}

export function useUpdateRecurringInvoice(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<RecurringInvoice>) => {
      const res = await apiClient.patch(`/sales/subscriptions/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'subscriptions'] })
      qc.invalidateQueries({ queryKey: subscriptionKeys.detail(id) })
    },
  })
}

export function usePauseRecurringInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/subscriptions/${id}/pause/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'subscriptions'] })
    },
  })
}

export function useResumeRecurringInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/subscriptions/${id}/resume/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'subscriptions'] })
    },
  })
}

export function useCancelRecurringInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/subscriptions/${id}/cancel/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'subscriptions'] })
    },
  })
}

export interface RunNowResponse {
  detail: string
  document_id: string
  document_status: string
  next_generation_date: string | null
}

export function useRunNowRecurringInvoice() {
  const qc = useQueryClient()
  return useMutation<RunNowResponse, unknown, string>({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/subscriptions/${id}/run-now/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'subscriptions'] })
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

// ─── VEN-08: Contratos ────────────────────────────────────────────────────────

export type ContractStatus = 'draft' | 'active' | 'expired' | 'cancelled' | 'renewed'

export interface Contract {
  id: string
  contract_number: string
  customer: string
  customer_name: string
  seller: string | null
  seller_name: string
  title: string
  start_date: string
  end_date: string | null
  status: ContractStatus
  status_display?: string
  is_expired: boolean
  contract_value: string | null
  currency: string
  purchase_order: string
  payment_method: 'contado' | 'credito'
  payment_term_days: number
  notes: string
  personalizados?: Record<string, string> | unknown[]
  subscriptions_count?: number
  documents_count?: number
  created_at: string
  updated_at?: string
}

export interface ContractListResponse {
  contracts: Contract[]
  count: number
  page: number
  page_size: number
  pages: number
}

export const contractKeys = {
  list: (params?: object) => ['sales', 'contracts', params] as const,
  detail: (id: string) => ['sales', 'contracts', id] as const,
}

export function useContracts(params?: {
  status?: string
  customer_id?: string
  seller_id?: string
  search?: string
  page?: number
  page_size?: number
}) {
  return useQuery<ContractListResponse>({
    queryKey: contractKeys.list(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/contracts/', { params })
      return res.data
    },
  })
}

export function useContract(id: string) {
  return useQuery<Contract>({
    queryKey: contractKeys.detail(id),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/contracts/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Contract>) => {
      const res = await apiClient.post('/sales/contracts/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'contracts'] })
    },
  })
}

export function useUpdateContract(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<Contract>) => {
      const res = await apiClient.patch(`/sales/contracts/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'contracts'] })
      qc.invalidateQueries({ queryKey: contractKeys.detail(id) })
    },
  })
}

export function useCancelContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/contracts/${id}/cancel/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'contracts'] })
      qc.invalidateQueries({ queryKey: ['sales', 'subscriptions'] })
    },
  })
}

export function useRenewContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, start_date, end_date }: { id: string; start_date?: string; end_date?: string }) => {
      const res = await apiClient.post(`/sales/contracts/${id}/renew/`, { start_date, end_date })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'contracts'] })
    },
  })
}

// ─── Plantillas de Documento ──────────────────────────────────────────────────

export function useDocumentTemplates(params?: { search?: string; page?: number; page_size?: number }) {
  return useQuery<DocumentTemplateListResponse>({
    queryKey: salesKeys.templates(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/templates/', { params })
      return res.data
    },
  })
}

export function useDocumentTemplate(id: string) {
  return useQuery<DocumentTemplate>({
    queryKey: salesKeys.template(id),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/templates/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateDocumentTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<DocumentTemplate>) => {
      const res = await apiClient.post('/sales/templates/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'templates'] })
    },
  })
}

export function useUpdateDocumentTemplate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<DocumentTemplate>) => {
      const res = await apiClient.patch(`/sales/templates/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'templates'] })
      qc.invalidateQueries({ queryKey: salesKeys.template(id) })
    },
  })
}

export function useDeleteDocumentTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/sales/templates/${id}/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'templates'] })
    },
  })
}

export function useCreateDocumentFromTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ templateId, customer }: { templateId: string; customer?: string }) => {
      const res = await apiClient.post(`/sales/templates/${templateId}/create-document/`, { customer })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

export function useSaveDocumentAsTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ documentId, name, description }: { documentId: string; name: string; description?: string }) => {
      const res = await apiClient.post(`/sales/documents/${documentId}/save-as-template/`, { name, description })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'templates'] })
    },
  })
}

export function useVoucherPreview(documentId: string | null, enabled = false) {
  return useQuery<VoucherPreviewResponse>({
    queryKey: salesKeys.voucherPreview(documentId ?? ''),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/documents/${documentId}/voucher-preview/`)
      return res.data
    },
    enabled: !!documentId && enabled,
    staleTime: 30 * 1000,
  })
}


// ─── PED-01: Pedidos de Venta — Hooks ────────────────────────────────────────

export function useSalesOrders(params?: {
  search?: string
  status?: string
  customer_id?: string
  seller_id?: string
  page?: number
  page_size?: number
  ordering?: string
}) {
  return useQuery<SalesOrderListResponse>({
    queryKey: salesKeys.orders(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/orders/', { params })
      return res.data
    },
  })
}

/** Abre el PDF (cotización) del pedido en una pestaña nueva. */
export async function openSalesOrderPdf(id: string) {
  const res = await apiClient.get(`/sales/orders/${id}/pdf/`, { responseType: 'blob' })
  const blobUrl = URL.createObjectURL(res.data)
  window.open(blobUrl, '_blank')
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
}

export function useSalesOrder(id: string | null) {
  return useQuery<SalesOrder>({
    queryKey: salesKeys.order(id ?? ''),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/orders/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateSalesOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post('/sales/orders/', data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
    },
  })
}

export function useCloneSalesOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/orders/${id}/duplicate/`)
      return res.data as { id: string }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
    },
  })
}

export function useUpdateSalesOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await apiClient.patch(`/sales/orders/${id}/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
    },
  })
}

export function useApproveSalesOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/orders/${id}/approve/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
    },
  })
}

export interface SalesOrderComment {
  id: string
  order: string
  author: string | null
  author_name: string
  comment_type: 'comment' | 'correction_request'
  comment_type_display: string
  text: string
  created_at: string
}

export function useSalesOrderComments(orderId: string | null) {
  return useQuery({
    queryKey: ['sales', 'order-comments', orderId],
    queryFn: async () => {
      const res = await apiClient.get<{ comments: SalesOrderComment[] }>(`/sales/orders/${orderId}/comments/`)
      return res.data
    },
    enabled: !!orderId,
  })
}

export function useCreateSalesOrderComment(orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { text: string; comment_type?: 'comment' | 'correction_request' }) => {
      const res = await apiClient.post<SalesOrderComment>(`/sales/orders/${orderId}/comments/`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'order-comments', orderId] })
    },
  })
}

export function useInvoiceSalesOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post(`/sales/orders/${id}/invoice/`)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}

export function useCancelSalesOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const res = await apiClient.post(`/sales/orders/${id}/cancel/`, {
        cancellation_reason: reason || '',
      })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'orders'] })
    },
  })
}

export function useCommissionDashboard(params?: {
  seller_id?: string
  date_from?: string
  date_to?: string
}) {
  return useQuery<CommissionDashboardData>({
    queryKey: salesKeys.commissions(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/commissions/', { params })
      return res.data
    },
  })
}

export function useProductCommissionHistory(productId: string | null) {
  return useQuery<CommissionHistoryResponse>({
    queryKey: ['sales', 'products', productId, 'commission-history'],
    queryFn: async () => {
      const res = await apiClient.get(`/sales/products/${productId}/commission-history/`)
      return res.data
    },
    enabled: !!productId,
  })
}


// ─── Forecast — Proyección de Ventas ─────────────────────────────────────────

export interface ForecastMonth {
  month: string
  recurring: string
  pipeline: string
  trend: string
  total: string
  commission: string
}

export interface HistoricalMonth {
  month: string
  total: string
  doc_count: number
  commission: string
}

export interface ForecastData {
  currency: string
  historical: HistoricalMonth[]
  forecast: ForecastMonth[]
  kpis: {
    total_forecast: string
    avg_monthly: string
    recurring_revenue: string
    pipeline_value: string
    growth_rate: string
    total_commission_forecast: string
    avg_commission_monthly: string
    avg_commission_rate: string
  }
  summary_by_source: {
    recurring_pct: string
    pipeline_pct: string
    trend_pct: string
  }
}

export function useForecast(params?: { months_back?: number; months_forward?: number }) {
  return useQuery<ForecastData>({
    queryKey: salesKeys.forecast(params),
    queryFn: async () => {
      const res = await apiClient.get('/sales/forecast/', { params })
      return res.data
    },
  })
}


// ─── Enviar documento por email ──────────────────────────────────────────────

export interface SendEmailSuggestions {
  suggested_to: string[]
  suggested_cc: string[]
  customer_name: string
}

export function useDocumentEmailSuggestions(documentId: string | null) {
  return useQuery<SendEmailSuggestions>({
    queryKey: ['sales', 'document-email', documentId],
    queryFn: async () => {
      const res = await apiClient.get(`/sales/documents/${documentId}/send-email/`)
      return res.data
    },
    enabled: !!documentId,
  })
}

export function useSendDocumentEmail() {
  return useMutation({
    mutationFn: async (data: {
      documentId: string
      to: string[]
      cc: string[]
      include_xml: boolean
    }) => {
      const res = await apiClient.post(`/sales/documents/${data.documentId}/send-email/`, {
        to: data.to,
        cc: data.cc,
        include_xml: data.include_xml,
      })
      return res.data
    },
  })
}

// ─── Adjuntos — Pedidos de Venta ─────────────────────────────────────────────

export function useOrderAttachments(orderId: string | null) {
  return useQuery<AttachmentListResponse>({
    queryKey: salesKeys.orderAttachments(orderId ?? ''),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/orders/${orderId}/attachments/`)
      return res.data
    },
    enabled: !!orderId,
  })
}

// Subida directa (sin hook) — usada para subir adjuntos buffereados justo
// después de crear el pedido, cuando todavía no hay un hook ligado al id.
export async function uploadOrderAttachmentFile(orderId: string, file: File): Promise<SalesAttachment> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await apiClient.post(
    `/sales/orders/${orderId}/attachments/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return res.data as SalesAttachment
}

export function useUploadOrderAttachment(orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => uploadOrderAttachmentFile(orderId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: salesKeys.orderAttachments(orderId) })
    },
  })
}

export function useDeleteOrderAttachment(orderId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      await apiClient.delete(`/sales/orders/${orderId}/attachments/${attachmentId}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: salesKeys.orderAttachments(orderId) })
    },
  })
}

// ─── Adjuntos — Documentos de Venta ─────────────────────────────────────────

export function useDocumentAttachments(documentId: string | null) {
  return useQuery<AttachmentListResponse>({
    queryKey: salesKeys.documentAttachments(documentId ?? ''),
    queryFn: async () => {
      const res = await apiClient.get(`/sales/documents/${documentId}/attachments/`)
      return res.data
    },
    enabled: !!documentId,
  })
}

export async function uploadDocumentAttachmentFile(documentId: string, file: File): Promise<SalesAttachment> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await apiClient.post(
    `/sales/documents/${documentId}/attachments/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )
  return res.data as SalesAttachment
}

export function useUploadDocumentAttachment(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => uploadDocumentAttachmentFile(documentId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: salesKeys.documentAttachments(documentId) })
    },
  })
}

export function useDeleteDocumentAttachment(documentId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      await apiClient.delete(`/sales/documents/${documentId}/attachments/${attachmentId}/`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: salesKeys.documentAttachments(documentId) })
    },
  })
}

// ─── Comunicacion de baja (Peru) ──────────────────────────────────────────────

/**
 * Estado de la comunicacion de baja de un comprobante.
 *
 * NO es lo mismo que anular: `cancel/` anula internamente un borrador o un
 * rechazado, y esto le pide a SUNAT dejar sin efecto un comprobante que ya
 * acepto. Tiene plazo (7 dias desde la aceptacion) y puede ser rechazada.
 */
export interface DocumentVoidStatus {
  can_void: boolean
  /** Por que no se puede, en palabras que el usuario pueda accionar. */
  reason_blocked: string
  code: string
  /** Dias que quedan de plazo. Negativo si ya vencio. */
  days_left: number | null
  deadline: string | null
  /**
   * Por que via se anula ante SUNAT. NO son el mismo tramite:
   *  - `void_communication` (RA): factura y notas.
   *  - `daily_summary` (RC): boleta, informada con estado «anulado» en el
   *    resumen diario que envia el facturador.
   */
  mechanism: '' | 'void_communication' | 'daily_summary'
  last_request: {
    id: string
    status: 'pending' | 'sent' | 'accepted' | 'rejected' | 'invalid' | 'error'
    status_label: string
    reason: string
    ticket: string
    response_note: string
    requested_at: string
  } | null
}

/**
 * Se consulta al ABRIR el modal, no por cada fila del listado: preguntarlo en la
 * tabla seria un request por documento para una accion que casi nunca se usa.
 */
export function useDocumentVoidStatus(documentId: string | null) {
  return useQuery<DocumentVoidStatus>({
    queryKey: ['sales', 'documents', documentId, 'void'],
    queryFn: async () => {
      const res = await apiClient.get(`/sales/documents/${documentId}/void/`)
      return res.data
    },
    enabled: !!documentId,
  })
}

export function useVoidDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await apiClient.post(`/sales/documents/${id}/void/`, { reason })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales', 'documents'] })
    },
  })
}
